import { supabase } from '../../sistema/services/supabaseClient';

// interface ChatMessage {
//   role: 'system' | 'user' | 'assistant';
//   content: string;
// }

interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// ---------- MODELOS / TIERS ----------
const ENV = (k:string) => (typeof process !== 'undefined' && (process as any)?.env?.[k]) || '';
const MODELS = {
  fast:      ENV('MODEL_FAST')      || 'o4-mini',
  quality:   ENV('MODEL_QUALITY')   || 'gpt-4o',
  reasoning: ENV('MODEL_REASONING') || 'o3',
  max:       ENV('MODEL_MAX')       || 'gpt-5',
} as const;

function pickTextDefault() {
  return ENV('MODEL_CHAT') || MODELS.fast;
}

// Extrai texto tanto do Chat Completions quanto do Responses API
function extractTextFromAnyResponse(data: any): string | undefined {
  // Chat Completions
  const compat = data?.choices?.[0]?.message?.content;
  if (compat != null) return typeof compat === 'string' ? compat : JSON.stringify(compat);
  // Responses API (conveniência)
  if (typeof data?.output_text === 'string' && data.output_text.trim()) return data.output_text;
  // Responses API (output[].content[].text)
  if (Array.isArray(data?.output)) {
    try {
      const joined = data.output
        .map((o: any) =>
          (o?.content || [])
            .map((p: any) => p?.text)
            .filter(Boolean)
            .join('')
        )
        .filter(Boolean)
        .join('\n')
        .trim();
      if (joined) return joined;
    } catch {}
  }
  // Responses API (message.content[].text)
  if (Array.isArray(data?.message?.content)) {
    const t = data.message.content.map((c: any) => c?.text).filter(Boolean).join('').trim();
    if (t) return t;
  }
  return undefined;
}

function buildUserPromptAlteracoes(userQuestion: string) {
  return `Tarefa: Levantar ALTERAÇÕES DE PROJETO desta obra a partir do "Contexto da Obra" abaixo.

Formato de resposta:
1) **Alterações de projeto detectadas**
   - (data) – (tipo) – (descrição curta)
   - Fonte: (Tarefa #ID ou nome), (Diário #ID/data), (Checklist item/template), (Documento nome/referência)
2) **Impactos (prazo/custo/escopo)** (se houver indícios)
3) **Próximos passos** (quem consultar, qual documento confirmar)

Além do texto acima, se possível, inclua AO FINAL um bloco JSON válido (apenas 1, sem comentários) dentro de \`\`\`json ... \`\`\`
com o seguinte formato:
{
  "evidencias": [
    {"data":"YYYY-MM-DD","origem":"tarefa|relatorio|comentario|servico|subetapa|requisicao","origem_id":"uuid","titulo":"...","fonte":"string curta"}
  ]
}

Pergunta do usuário: ${userQuestion}

Se não encontrar nada, responda mesmo assim com a seção 1 dizendo "Sem registros..." e preencha os passos do item 3.`;
}

class OpenAIService {
  private tryParseJson(text: any): any | undefined {
    if (!text) return undefined;
    if (typeof text === 'object') return text; // caso raro
    if (typeof text === 'string') {
      try { return JSON.parse(text); } catch {}
      const m = text.match(/```json\s*([\s\S]*?)```/i);
      if (m) { try { return JSON.parse(m[1]); } catch {} }
    }
    return undefined;
  }

  // NOVO: chat com response_format suportado
  async chat(opts: {
    model?: string;
    messages: Array<{ role:'system'|'user'|'assistant', content:any }>;
    response_format?: any;
    max_tokens?: number;
    temperature?: number;
    stream?: boolean;
  }): Promise<{ parsed?: any; text: string }> {
    const model = opts.model || pickTextDefault();
    const body: any = {
      model,
      messages: opts.messages || [],
      max_tokens: Math.min(opts.max_tokens ?? 5000, 12000),
      temperature: opts.temperature ?? 0.2,
      stream: opts.stream ?? false,
      ...(opts.response_format ? { response_format: opts.response_format } : {})
    };

    // 1) Tenta Edge Function (com compat 'chat' p/ manter formato antigo)
    try {
      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: { ...body, compat: 'chat' }
      });
      const textCompat = extractTextFromAnyResponse(data);
      if (!error && textCompat != null) {
        const parsed = opts.response_format ? this.tryParseJson(textCompat) : undefined;
        return { text: textCompat, parsed };
      }
    } catch {} // cai para chamada direta

    // 2) Direto na API (se houver key no client)
    const apiKey = this.getAPIKey();
    if (!apiKey) return { text: '', parsed: undefined };
    const direct = await this.directOpenAICall(body, apiKey);
    const text = direct?.choices?.[0]?.message?.content || '';
    const parsed = opts.response_format ? this.tryParseJson(text) : undefined;
    return { text, parsed };
  }

  // 🔧 PROMPT ESPECIALIZADO PARA MATERIAL MATCHING
  // private readonly materialMatchingPrompt = `
// 🎯 **ASSISTENTE ESPECIALIZADO EM MATERIAIS DE CONSTRUÇÃO - ACOR ENGENHARIA**

// Você é um especialista em identificação e matching de materiais de construção civil para a ACOR Engenharia de Florianópolis/SC.

// === VALORES ACOR ===
// 🏃‍♂️ AGILIDADE: Análises rápidas e precisas
// 🤝 CONFIANÇA: Matches técnicos corretos
// 📋 ORGANIZAÇÃO: Respostas estruturadas
// 🎯 RESULTADO: Qualidade na identificação

// === ESPECIALIDADE PRINCIPAL ===
// ✅ Identificação precisa de materiais a partir de texto coloquial
// ✅ Matching semântico inteligente (ex: "tubo esgoto 50" → "TUBO PVC ESGOTO 50MM")
// ✅ Análise de especificações técnicas críticas
// ✅ Diferenciação entre produtos similares mas distintos
// ✅ Categorização por tipo de material

// === REGRAS TÉCNICAS CRÍTICAS ===
// ⚠️ **DIMENSÕES**: 25mm ≠ 50mm (NUNCA misturar)
// ⚠️ **BITOLAS**: 1.5mm² ≠ 4.0mm² (especificações exatas)
// ⚠️ **TIPOS**: CP-II ≠ CP-III (cimentos diferentes)
// ⚠️ **APLICAÇÕES**: soldável ≠ roscável (conexões distintas)

// === LINGUAGEM COLOQUIAL vs TÉCNICA ===
// • "tubo esgoto 50" → TUBO PVC ESGOTO 50MM
// • "fita veda rosca" → FITA VEDA ROSCA TEFLON
// • "cimento" → CIMENTO PORTLAND CP-II
// • "cabo 2.5" → CABO FLEXÍVEL 2.5MM²
// • "joelho 90" → JOELHO PVC 90° 

// === FORMATO DE RESPOSTA OBRIGATÓRIO ===
// SEMPRE responda em JSON válido seguindo esta estrutura exata:

// {
//   "material_id": "uuid-do-material-encontrado-ou-null",
//   "confianca": 0-100,
//   "justificativa": "explicação técnica da escolha",
//   "categoria_detectada": "TUBO|CIMENTO|CABO|FITA|JOELHO|etc",
//   "especificacao_detectada": "dimensão-ou-tipo-identificado",
//   "alternativas": [
//     {
//       "material_id": "uuid-alternativa",
//       "confianca": 0-100,
//       "motivo": "por que é alternativa viável"
//     }
//   ]
// }

// === NÍVEIS DE CONFIANÇA ===
// • 90-100%: Match perfeito (mesma especificação técnica)
// • 70-89%: Match muito provável (nomenclatura ligeiramente diferente)
// • 50-69%: Match possível (categoria correta, verificar especificação)
// • 0-49%: Não recomendado (retornar material_id: null)

// === INSTRUÇÕES IMPORTANTES ===
// 🔍 Se não encontrar match adequado, retorne material_id: null
// 🎯 Priorize QUALIDADE sobre quantidade de resultados
// ⚠️ Em caso de dúvida sobre especificação técnica, seja conservador
// 📋 SEMPRE inclua justificativa técnica detalhada
// `;

  // 🔧 PROMPT GENÉRICO PARA OUTRAS CONSULTAS
  // private readonly basePrompt = `
// Você é o ASSISTENTE VIRTUAL da ACOR Engenharia, empresa de construção civil de alto padrão em Florianópolis/SC.

// === VALORES ACOR ===
// 🏃‍♂️ AGILIDADE: Respostas rápidas e eficientes
// 🤝 CONFIANÇA: Informações precisas e transparentes  
// 📋 ORGANIZAÇÃO: Processos estruturados e claros
// 🎯 RESULTADO: Foco na entrega de qualidade

// Seja direto, técnico mas acessível, e mantenha foco na construção civil.
// `;

  /**
   * 🔧 FUNÇÃO PRINCIPAL: Processar matching de materiais
   */
  async processTextMessage(
    userMessage: string,
    obraContext?: string,
    // messageHistory: ChatMessage[] = []
  ): Promise<string> {
    try {
      const apiKey = this.getAPIKey();

      // Determinar tipo de consulta
      const isMatching = this.isMatchingQuery(userMessage, obraContext);

      // Preparar contexto baseado no tipo de consulta
      let systemPrompt = '';
      let userPrompt = '';

      if (isMatching) {
        systemPrompt = `Você é um assistente especializado em materiais de construção. 
        Sua função é ajudar a encontrar e analisar materiais específicos.
        Sempre responda de forma clara e objetiva, fornecendo informações práticas.`;
        
        userPrompt = `Analise a seguinte solicitação sobre materiais: "${userMessage}"
        Forneça uma resposta útil e específica para o contexto de construção.`;
      } else {
        systemPrompt = `Você é um assistente especializado em gestão de obras e construção.
        Sua função é ajudar com questões gerais sobre cronogramas, tarefas e gestão de projetos.
        Sempre responda de forma clara e objetiva.`;
        
        userPrompt = `Responda à seguinte pergunta sobre gestão de obras: "${userMessage}"`;
      }

      // 1) Tentar Edge Function primeiro (usa a key segura do servidor)
      try {
        const { data, error } = await supabase.functions.invoke('openai-chat', {
          body: {
            // 🔁 Defaults mais fortes para extração/entendimento
            // (mantidos iguais ao que você já usa)
            model: ((typeof process !== 'undefined' && (process as any)?.env?.MODEL_CHAT) || 'gpt-4o'),
            messages: [
              { role: 'system', content: systemPrompt },
              ...(obraContext ? [{ role: 'system', content: `Contexto da Obra (somente leitura):\n${obraContext}` }] : []),
              { role: 'user', content: userPrompt }
            ],
            max_tokens: 5000,
            temperature: 0.2,
            compat: 'chat'
          }
        });
        const textCompat = extractTextFromAnyResponse(data);
        if (!error && textCompat != null) {
          return textCompat;
        }
      } catch (_edgeErr) {
        // segue para fallback
      }

      // 2) Chamada direta somente se houver API key no client
      if (!apiKey) {
        throw new Error('API Key não configurada (Edge Function indisponível). Configure a Edge Function "openai-chat" OU defina OPENAI_API_KEY no ambiente do client para fallback.');
      }

      // Chamada direta para OpenAI (uso de key local)
      const openaiResponse = await this.directOpenAICall({
        model: ((typeof process !== 'undefined' && (process as any)?.env?.MODEL_CHAT) || 'gpt-4o'),
        messages: [
          { role: 'system', content: systemPrompt },
          ...(obraContext ? [{ role: 'system', content: `Contexto da Obra (somente leitura):\n${obraContext}` }] : []),
          { role: 'user', content: userPrompt }
        ],
        max_tokens: 5000,
        temperature: 0.2
      }, apiKey);

      return openaiResponse.choices[0].message.content;

    } catch (error) {
      console.error('❌ Erro OpenAI API:', error);
      // Mensagem mais clara para a UI (sem quebrar a página)
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      throw new Error(`Erro ao processar consulta: ${msg}`);
    }
  }

  /**
   * 🖼️ Processar mensagem multimodal (texto + 1 imagem base64/URL)
   */
  async processMultimodalMessage(
    userMessage: string,
    imageUrl: string,
    obraContext?: string
  ): Promise<string> {
    const apiKey = this.getAPIKey();
    if (!apiKey) throw new Error('API Key não configurada');

    const systemPrompt = `Você é um assistente técnico da ACOR Engenharia (somente leitura).
- Responda com objetividade e orientações seguras (sem prescrever obra sem vistoria).
- Se houver potencial risco estrutural, recomende avaliação presencial de engenheiro.`;

    const messages: any[] = [
      { role: 'system', content: systemPrompt },
      ...(obraContext ? [{ role: 'system', content: `Contexto da Obra (somente leitura):\n${obraContext}` }] : []),
      {
        role: 'user',
        content: [
          { type: 'text', text: userMessage || 'Analise a imagem da obra.' },
          { type: 'image_url', image_url: { url: imageUrl } }
        ]
      }
    ];

    // 1) Tentar Edge Function primeiro (usa a key segura do servidor)
    try {
      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          model: ((typeof process !== 'undefined' && (process as any)?.env?.MODEL_CHAT) || 'gpt-4o'),
          messages,
          max_tokens: 5000,
          temperature: 0.2,
          compat: 'chat'
        }
      });
      const textCompat = extractTextFromAnyResponse(data);
      if (!error && textCompat != null) return textCompat;
    } catch {}

    // 2) fallback direto
    const direct = await this.directOpenAICall({
      model: ((typeof process !== 'undefined' && (process as any)?.env?.MODEL_CHAT) || 'gpt-4o'),
      messages,
      max_tokens: 5000,
      temperature: 0.2
    }, apiKey as string);

    return direct.choices?.[0]?.message?.content || 'Sem resposta do modelo.';
  }

  /**
   * 🔍 Detectar se é query de matching de material
   */
  private isMatchingQuery(message: string, context?: string): boolean {
    // Se tem contexto com CATÁLOGO DE MATERIAIS, é matching
    if (context?.includes('CATÁLOGO DE MATERIAIS DISPONÍVEIS')) {
      return true;
    }

    // Se tem palavras-chave de matching
    const matchingKeywords = [
      'MATCHING INTELIGENTE',
      'material_id',
      'confianca',
      'categoria_detectada'
    ];

    return matchingKeywords.some(keyword => 
      message.includes(keyword) || context?.includes(keyword)
    );
  }

  /**
   * 🔧 Verificar se resposta é JSON válido
   */
  // private isValidJSON(text: string): boolean {
  //   try {
  //     const jsonMatch = text.match(/\{[\s\S]*\}/);
  //     if (jsonMatch) {
  //       JSON.parse(jsonMatch[0]);
  //       return true;
  //     }
  //     return false;
  //   } catch {
  //     return false;
  //   }
  // }

  /**
   * 🔧 Chamar OpenAI de forma segura
   */
  private async callOpenAI(payload: any): Promise<OpenAIResponse> {
    // 🔍 1. Verificar se API key está configurada
    const apiKey = this.getAPIKey();
    if (!apiKey) {
      throw new Error('API_KEY_NOT_CONFIGURED');
    }

    console.log('🔑 API Key configurada:', !!apiKey);

    try {
      // 🚀 2. Tentar Edge Function primeiro (se existir)
      console.log('⚡ Tentando Edge Function...');
      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: payload
      });

      if (!error && data) {
        console.log('✅ Edge Function funcionou');
        return data;
      }

      console.log('⚠️ Edge Function falhou, usando chamada direta:', error);

    } catch (edgeError) {
      console.log('⚠️ Edge Function não disponível:', edgeError);
    }

    // 🔄 3. Fallback: Chamada direta
    console.log('🔄 Usando chamada direta OpenAI...');
    return await this.directOpenAICall(payload, apiKey);
  }

  /**
   * 🔑 Obter API Key de forma segura
   */
  private getAPIKey(): string | null {
    // Ordem de prioridade para API Key
    const sources = [
      () => process.env.OPENAI_API_KEY,
      () => localStorage.getItem('openai_api_key'),
      () => sessionStorage.getItem('openai_api_key')
    ];

    for (const getKey of sources) {
      try {
        const key = getKey();
        if (key && key.startsWith('sk-')) {
          return key;
        }
      } catch (error) {
        // Ignorar erros de acesso (ex: localStorage em contexto seguro)
      }
    }

    return null;
  }

  /**
   * 🔑 Definir API Key no client (apenas DEV!)
   */
  public setAPIKey(key: string) {
    try { localStorage.setItem('openai_api_key', key); } catch {}
  }

  /**
   * 🗑️ Remover API Key do client (apenas DEV!)
   */
  public clearAPIKey() {
    try { localStorage.removeItem('openai_api_key'); } catch {}
    try { sessionStorage.removeItem('openai_api_key'); } catch {}
  }

  /**
   * 🔄 Chamada direta OpenAI
   */
  private async directOpenAICall(payload: any, apiKey: string): Promise<OpenAIResponse> {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ Erro OpenAI API:', error);
      
      // Tratar erros específicos
      if (response.status === 401) {
        throw new Error('INVALID_API_KEY');
      } else if (response.status === 429) {
        throw new Error('RATE_LIMIT_EXCEEDED');
      } else if (response.status === 402) {
        throw new Error('INSUFFICIENT_QUOTA');
      }
      
      throw new Error(`OpenAI API Error: ${error.error?.message || 'Unknown error'}`);
    }

    const result = await response.json();
    console.log('✅ OpenAI chamada direta sucesso');
    return result;
  }

  /**
   * ❌ Mensagens de erro específicas
   */
  // private getErrorMessage(error: any): string {
  //   const errorType = error.message || 'UNKNOWN_ERROR';

  //   switch (errorType) {
  //     case 'API_KEY_NOT_CONFIGURED':
  //       return `🔑 **API Key OpenAI não configurada**

  // **Para configurar:**
  // \`\`\`javascript
  // // No console do browser:
  // localStorage.setItem('openai_api_key', 'sk-sua-api-key-aqui');
  // \`\`\`

  // **Ou configure como variável de ambiente:**
  // \`OPENAI_API_KEY=sk-sua-api-key-aqui\`

  // **Como obter API Key:**
  // 1. Acesse https://platform.openai.com/api-keys
  // 2. Faça login na sua conta OpenAI
  // 3. Crie uma nova API Key
  // 4. Configure no sistema usando um dos métodos acima

  // ⚠️ **Importante**: Mantenha sua API Key segura e privada!`;

  //     case 'INVALID_API_KEY':
  //       return `❌ **API Key OpenAI inválida**

  // A API Key configurada não é válida ou expirou.

  // **Verificações:**
  // • API Key deve começar com 'sk-'
  // • Verifique se não expirou
  // • Confirme se tem créditos disponíveis
  // • Teste no dashboard OpenAI

  // **Reconfigurar:**
  // \`localStorage.setItem('openai_api_key', 'sk-nova-api-key-aqui');\``;

  //     case 'RATE_LIMIT_EXCEEDED':
  //       return `⚠️ **Limite de taxa excedido**

  // Muitas requisições em pouco tempo.

  // **Aguarde alguns segundos e tente novamente.**
  // O sistema voltará ao normal automaticamente.`;

  //     case 'INSUFFICIENT_QUOTA':
  //       return `💳 **Cota OpenAI insuficiente**

  // Créditos da API OpenAI esgotados.

  // **Soluções:**
  // • Adicione créditos em https://platform.openai.com/account/billing
  // • Verifique limites de uso da conta
  // • Configure método de pagamento`;

  //     default:
  //       return `🤖 **Erro temporário na IA**

  // **Detalhes:** ${errorType}

  // **Tente:**
  // • Aguarde alguns segundos e tente novo
  // • Verifique sua conexão
  // • Verifique configuração da API Key

  // **Para suporte:** Entre em contato se o problema persistir.`;
  //   }
  // }

  /**
   * 📊 Verificar status da configuração OpenAI
   */
  async verificarStatus(): Promise<{
    configurado: boolean;
    apiKey: boolean;
    conectividade: boolean;
    erro?: string;
  }> {
    try {
      const apiKey = this.getAPIKey();
      
      if (!apiKey) {
        return {
          configurado: false,
          apiKey: false,
          conectividade: false,
          erro: 'API Key não configurada'
        };
      }

      // Teste simples de conectividade
      const testResponse = await this.callOpenAI({
        model: 'gpt-3.5-turbo',
        messages: [{ role: 'user', content: 'teste' }],
        max_tokens: 5
      });

      return {
        configurado: true,
        apiKey: true,
        conectividade: !!testResponse.choices?.[0]?.message?.content,
        erro: undefined
      };

    } catch (error) {
      return {
        configurado: false,
        apiKey: !!this.getAPIKey(),
        conectividade: false,
        erro: error instanceof Error ? error.message : 'Erro desconhecido'
      };
    }
  }
}

export const openaiService = new OpenAIService();
export { buildUserPromptAlteracoes };